import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ApiService } from '@services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, isNil } from 'lodash';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, map } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  CompanyCustomSpread,
  CompanyCustomSpreadResponse,
  CurrencyInfo,
  CustomSpread,
  DEFAULT_SPREAD,
  MAX_SPREAD,
  MIN_SPREAD,
  ModalStep,
} from './custom-spread-dialog.types';
import { deepClone } from '@utils/deep-clone.utils';

@Component({
  selector: 'ew-custom-spread-dialog',
  templateUrl: './custom-spread-dialog.component.html',
  styleUrls: ['./custom-spread-dialog.component.scss'],
})
export class CustomSpreadDialogComponent implements OnInit {
  isLoading: boolean = true;
  isProcessing: boolean = false;
  hasError: boolean = true;
  errorMessage: string = '';
  companyCustomSpread?: CompanyCustomSpread;
  customSpreads: CustomSpread[] = [];
  modalStep: ModalStep = ModalStep.TERMS_AND_CONDITIONS;
  termsAccepted: boolean = false;

  // Properties for spread list functionality
  spreadsForm: FormGroup;
  originalSpreads: CustomSpread[] = [];
  hasChanges: boolean = false;
  isEditing: boolean = false;
  currencies: CurrencyInfo[] = [];
  isLoadingCurrencies: boolean = true;
  validationError?: string;
  minSpread: number = MIN_SPREAD;
  maxSpread: number = MAX_SPREAD;

  constructor(
    private API: ApiService,
    private fb: FormBuilder,
    private http: HttpClient,
    public dialogRef: MatDialogRef<CustomSpreadDialogComponent>,
    private translate: TranslateService,
  ) {
    this.spreadsForm = this.fb.group({
      spreads: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.getCompanyCustomSpread();
  }

  get spreadsArray(): FormArray {
    return this.spreadsForm.get('spreads') as FormArray;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  handleClickConfirm(): void {
    if (this.modalStep === ModalStep.TERMS_AND_CONDITIONS) {
      this.acceptTermsAndConditions();
    } else if (this.modalStep === ModalStep.CUSTOM_SPREAD) {
      this.saveCustomSpread();
    }
  }

  isConfirmButtonDisabled(): boolean {
    if (this.modalStep === ModalStep.TERMS_AND_CONDITIONS) {
      return this.isProcessing || !this.termsAccepted;
    } else if (this.modalStep === ModalStep.CUSTOM_SPREAD) {
      return (
        this.isProcessing ||
        this.spreadsForm.invalid ||
        isEmpty(this.spreadsArray?.value)
      );
    }
    return false;
  }

  private acceptTermsAndConditions(): void {
    if (this.termsAccepted) {
      this.modalStep = ModalStep.CUSTOM_SPREAD;
      this.originalSpreads = deepClone(this.customSpreads);
      this.initForm(this.customSpreads);
    }
  }

  private checkIfSpreadIsValid(): boolean {
    const formSpreadsValue = this.spreadsArray.value as Array<{
      currency: string;
      spread: number | null;
    }>;

    this.hasError = false;
    const hasInvalidValue = formSpreadsValue.some(
      (form) =>
        isNil(form.spread) ||
        form.spread > this.maxSpread ||
        form.spread < this.minSpread,
    );
    if (hasInvalidValue) {
      const message = this.translate.instant(
        'settingsCustomSpread:spreadValueRange',
      );
      this.API.alertService.error(message);
      return false;
    }

    return true;
  }

  private saveCustomSpread(): void {
    const isCustomSpreadStep = this.modalStep === ModalStep.CUSTOM_SPREAD;
    const isSpreadValueValid = this.checkIfSpreadIsValid();

    if (
      isCustomSpreadStep &&
      (this.spreadsForm.invalid || !isSpreadValueValid)
    ) {
      return;
    }

    this.isProcessing = true;
    if (
      this.modalStep === ModalStep.CUSTOM_SPREAD &&
      this.spreadsArray.length > 0
    ) {
      const formSpreadsValue = this.spreadsArray.value as Array<{
        currency: string;
        spread: number | null;
      }>;

      const updatedSpreads = formSpreadsValue.reduce(
        (acc: CustomSpread[], currentFormSpread, index) => {
          const originalSpreadEntry = this.originalSpreads[index];

          const currentSpreadTransformedValue = isNil(currentFormSpread.spread)
            ? null
            : Number(currentFormSpread.spread).toString();

          const originalSpreadTransformedValue =
            isNil(originalSpreadEntry.spread) ||
            originalSpreadEntry.spread === ''
              ? null
              : originalSpreadEntry.spread;

          if (
            currentSpreadTransformedValue !== originalSpreadTransformedValue
          ) {
            acc.push({
              currency: currentFormSpread.currency,
              spread:
                currentSpreadTransformedValue === null
                  ? undefined
                  : currentSpreadTransformedValue,
            });
          }
          return acc;
        },
        [],
      );

      this.API.post('v1/custom-spread/save', { spreads: updatedSpreads })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.handleApiError(error.error, 'SAVE_CUSTOM_SPREAD');
            return of(null); // Return an observable for the stream to continue
          }),
          finalize(() => {
            this.isProcessing = false;
          }),
        )
        .subscribe((response: CompanyCustomSpreadResponse | null) => {
          if (response && response.success) {
            const successMessage = this.translate.instant(
              'CUSTOM_SPREAD_DIALOG.SAVED_SUCCESSFULLY',
            );
            this.API.alertService.success(successMessage);
            this.dialogRef.close(true);
          } else if (response && !response.success) {
            this.handleApiError(response, 'SAVE_CUSTOM_SPREAD');
          } else if (!response && this.hasError) {
            // This case implies an error was caught by catchError and already handled by handleApiError
            // No further action needed here unless specific UI updates are required for this scenario
          }
        });
    } else {
      const spreadsToSave = this.customSpreads.map((spread) => ({
        currency: spread.currency,
        customSpread:
          isNil(spread.spread) || spread.spread === ''
            ? null
            : parseFloat(spread.spread.toString()).toString(),
      }));

      this.API.post('v1/custom-spread/save', { spreads: spreadsToSave })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.handleApiError(error.error, 'SAVE_CUSTOM_SPREAD_FALLBACK');
            return of(null); // Return an observable for the stream to continue
          }),
          finalize(() => {
            this.isProcessing = false;
          }),
        )
        .subscribe((response: CompanyCustomSpreadResponse | null) => {
          if (response && response.success) {
            const successMessage = this.translate.instant(
              'CUSTOM_SPREAD_DIALOG.SAVED_SUCCESSFULLY',
            );
            this.API.alertService.success(successMessage);
            this.dialogRef.close(true);
          } else if (response && !response.success) {
            this.handleApiError(response, 'SAVE_CUSTOM_SPREAD_FALLBACK');
          } else if (!response && this.hasError) {
            // Error already handled by catchError and handleApiError
          }
        });
    }
  }

  private getCompanyCustomSpread(): void {
    this.isLoading = true;
    this.hasError = false;

    this.API.get(`v1/custom-spread/list`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleApiError(error.error, 'GET_COMPANY_CUSTOM_SPREAD');
          return of(null);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe((response: CompanyCustomSpreadResponse | null) => {
        if (response && response.success) {
          this.hasError = !response.success;
          this.modalStep = response.records.isActive
            ? ModalStep.CUSTOM_SPREAD
            : ModalStep.TERMS_AND_CONDITIONS;
          this.companyCustomSpread = response?.records;
          this.customSpreads = response?.records?.customSpreads || [];
          this.isEditing = !this.customSpreads.some((spread) =>
            isNil(spread.spread),
          );

          // If terms are accepted and we're in the CUSTOM_SPREAD step, initialize the form
          if (this.modalStep === ModalStep.CUSTOM_SPREAD) {
            this.originalSpreads = deepClone(this.customSpreads);
            this.initForm(this.customSpreads);
          }
        }
      });
  }

  initForm(spreads: CustomSpread[]): void {
    // Clear existing form array
    while (this.spreadsArray.length) {
      this.spreadsArray.removeAt(0);
    }

    // Add form groups for each spread
    spreads.forEach((spread, index) => {
      // Improved handling of customSpread values
      let spreadValue = DEFAULT_SPREAD;
      if (
        spread.spread !== undefined &&
        spread.spread !== null &&
        spread.spread !== ''
      ) {
        spreadValue = parseFloat(spread.spread);
      }

      this.spreadsArray.push(
        this.fb.group({
          currency: [spread.currency],
          spread: [
            spreadValue,
            [
              Validators.required,
              Validators.min(this.minSpread),
              Validators.max(this.maxSpread),
            ],
          ],
        }),
      );
    });

    // Subscribe to form changes to detect modifications
    this.spreadsForm.valueChanges.subscribe(() => {
      this.checkForChanges();
    });
    this.checkForChanges();
  }

  checkForChanges(): void {
    if (!this.originalSpreads || this.originalSpreads.length === 0) {
      this.hasChanges = false;
      return;
    }

    const currentValues = this.spreadsForm.value.spreads;

    this.hasChanges = currentValues.some(
      (current: { currency: string; spread: number | null }, index: number) => {
        if (index >= this.originalSpreads.length) return true;
        const original = this.originalSpreads[index];

        const currentSpreadValue = current.spread === null ? 0 : current.spread;
        const originalSpreadValue = original.spread
          ? parseFloat(original.spread)
          : 0;

        return (
          !isNil(original.spread) && currentSpreadValue !== originalSpreadValue
        );
      },
    );
  }

  /**
   * Handles API errors from both HTTP errors and business validation errors
   * @param apiResponse The error response from the API
   * @param logContext The context for logging
   */
  private handleApiError(apiResponse: any, logContext: string): void {
    this.hasError = true;

    if (
      apiResponse &&
      Array.isArray(apiResponse.errors) &&
      apiResponse.errors.length > 0
    ) {
      // Process each error in the errors array
      apiResponse.errors.forEach((error: any) => {
        if (error && error.message) {
          // Extract the error code from the message (e.g., "spreads.0.INVALID_SPREAD_VALUE")
          const parts = error.message.split('.');
          const errorCode = parts[parts.length - 1];
          const translatedMessage = this.getTranslatedErrorMessage(errorCode);

          this.API.alertService.error(translatedMessage);
          this.errorMessage = translatedMessage;

          console.error(
            {
              level: 'error',
              message: `API validation error: ${errorCode}`,
              payload: apiResponse,
              error: {
                message: error.message,
                details: error.details || {},
              },
            },
            error.stack,
            `${logContext} | ERROR`,
          );
        }
      });
    } else {
      const defaultErrorMessage = this.translate.instant(
        'CUSTOM_SPREAD_DIALOG.ERRORS.DEFAULT_ERROR',
      );
      this.errorMessage = defaultErrorMessage;
      this.API.alertService.error(defaultErrorMessage);

      console.error({
        level: 'error',
        message: 'Failed to process custom spread operation',
        payload: apiResponse,
        error: {
          message: this.errorMessage,
        },
      });
    }
  }

  /**
   * Gets the translated error message for a specific error code
   * @param errorCode The error code from the API
   * @returns The translated error message
   */
  private getTranslatedErrorMessage(errorCode: string): string {
    const translationKey = `CUSTOM_SPREAD_DIALOG.ERRORS.${errorCode}`;
    const translatedMessage = this.translate.instant(translationKey);

    if (translatedMessage === translationKey) {
      return this.translate.instant(
        'CUSTOM_SPREAD_DIALOG.ERRORS.DEFAULT_ERROR',
      );
    }

    return translatedMessage;
  }
}
